<script setup>
import { ref, computed } from 'vue';
import { useStore } from 'vuex';
import BaseBubble from './Base.vue';
import MessageStatus from '../MessageStatus.vue';
import ContextMenu from 'dashboard/modules/conversations/components/MessageContextMenu.vue';
import { useSnakeCase } from 'dashboard/composables/useTransformKeys';
import { useMapGetter } from 'dashboard/composables/store';
import GalleryView from 'dashboard/components/widgets/conversation/components/GalleryView.vue';
import { useMessageContext } from '../provider.js';
import { useDisplaySenderName } from '../useDisplaySenderName.js';
import { computeMenuAnchor } from '../contextMenuAnchor.js';
import { messageTimestamp } from 'shared/helpers/timeHelper';
import { MESSAGE_STATUS, MESSAGE_TYPES, ORIENTATION } from '../constants';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { LocalStorage } from 'shared/helpers/localStorage';
import { LOCAL_STORAGE_KEYS } from 'dashboard/constants/localStorage';

// Aceleres: 2+ stickers consecutivos, estilo WhatsApp — 2 por linha, cada
// um repetindo a MESMA estrutura de um sticker sozinho: nome do remetente
// em cima, imagem sem corte num quadro fixo de 190x190, horário embaixo
// COM o fundo verde/branco do balão por trás (não sobreposto por cima da
// imagem). Diferente do álbum de fotos (ImageAlbum.vue), que corta em
// quadrado dentro de UM cartão só: aqui cada sticker é seu próprio cartão
// completo, só que 2 lado a lado em vez de empilhados.
//
// Não dá pra reusar o BaseBubble por tile pra isso: o MessageMeta dele
// SEMPRE fica `absolute` (sobreposto), mesmo sem media-caption — é assim
// que a bolha de sticker sozinho já funciona hoje. Por isso o card de
// cada tile é montado na mão aqui (nome + imagem + horário em fluxo
// normal, cada um na sua própria linha), em vez de tentar torcer o
// BaseBubble pra um comportamento que ele não tem.

const IMAGE_BOX_PX = 190;

const { albumMessages, orientation, currentUserId, inboxSupportsReplyTo } =
  useMessageContext();

const store = useStore();
const currentAccountIdGetter = useMapGetter('getCurrentAccountId');
const conversationGetter = useMapGetter('getConversationById');

const albumItems = computed(() => {
  const list = albumMessages?.value || [];
  return list
    .map(message => ({
      message,
      attachment: (message.attachments && message.attachments[0]) || null,
    }))
    .filter(item => item.attachment);
});

const albumGalleryAttachments = computed(() =>
  albumItems.value.map(item => useSnakeCase(item.attachment))
);

// Aceleres: todo o run tem o mesmo remetente (garantido no agrupamento em
// MessageList.vue), então o nome do contexto vale pros dois tiles.
//
// Usa a MESMA resolução do BaseBubble. Ler `sender.name` direto aqui mostrava
// o dono do token admin da ponte WAME no lugar do rótulo do inbox, então o par
// de figurinhas trocava de autor exibido só por ter sido agrupado.
const senderName = useDisplaySenderName();

const tileBackgroundClass = computed(() =>
  orientation.value === ORIENTATION.RIGHT ? 'bg-[#d9fdd3]' : 'bg-white'
);
const tileRoundedClass = computed(() =>
  orientation.value === ORIENTATION.RIGHT
    ? 'rounded-xl ltr:rounded-br-sm rtl:rounded-bl-sm'
    : 'rounded-xl'
);

const readableTime = message => messageTimestamp(message?.createdAt, 'HH:mm');

const statusToShow = message => {
  if (
    message?.messageType !== MESSAGE_TYPES.OUTGOING ||
    message?.private ||
    message?.status === MESSAGE_STATUS.FAILED
  ) {
    return null;
  }

  if (
    [
      MESSAGE_STATUS.SENT,
      MESSAGE_STATUS.DELIVERED,
      MESSAGE_STATUS.READ,
    ].includes(message.status)
  ) {
    return message.status;
  }

  return MESSAGE_STATUS.PROGRESS;
};

// Aceleres: menu de contexto por sticker. Message.vue só sabe abrir o menu
// pra UMA mensagem raiz (a última do par, ver componentToRender); aqui cada
// tile guarda em `activeContextItem` QUAL sticker foi clicado e abre o MESMO
// componente de menu (ContextMenu = MessageContextMenu.vue) apontando pra
// essa mensagem especificamente — assim "encaminhar"/"reagir"/"favoritar"
// no sticker 1 nunca afeta o sticker 2, e vice-versa.
const showTileContextMenu = ref(false);
const tileContextMenuPosition = ref({});
const activeContextItem = ref(null);

const openTileContextMenu = (event, item) => {
  activeContextItem.value = item;
  tileContextMenuPosition.value = computeMenuAnchor(event, {
    anchor: event.currentTarget,
  });
  showTileContextMenu.value = true;
};

const closeTileContextMenu = () => {
  showTileContextMenu.value = false;
};

// Aceleres: mesmo cálculo do contextMenuEnabledOptions em Message.vue, mas
// simplificado pro que um sticker individual realmente oferece — sem os
// ramos de texto/edição/nota (copy, edit, cannedResponse, translate,
// addToNote), que já são `false` pra qualquer sticker mesmo na bolha normal.
const tileEnabledOptions = computed(() => {
  const message = activeContextItem.value?.message;
  if (!message) return {};

  const isFailedOrProcessing =
    message.status === MESSAGE_STATUS.FAILED ||
    message.status === MESSAGE_STATUS.PROGRESS;
  const isDeleted = !!message.contentAttributes?.deleted;

  return {
    info: !isFailedOrProcessing && !isDeleted,
    react: !isFailedOrProcessing && !isDeleted,
    download: false,
    attachmentType: 'image',
    copy: false,
    edit: false,
    delete: false,
    cannedResponse: false,
    copyLink: !isFailedOrProcessing,
    translate: false,
    replyTo:
      !message.private &&
      !!inboxSupportsReplyTo.value?.outgoing &&
      !isFailedOrProcessing,
    forward: !isFailedOrProcessing && !isDeleted,
    star: !isFailedOrProcessing && !isDeleted,
    addToNote: false,
    pinned:
      conversationGetter.value(message.conversationId)?.additional_attributes
        ?.pinned_message_id === message.id,
  };
});

// Aceleres: MessageContextMenu.vue (o componente de menu) espera a mensagem
// no MESMO formato que Message.vue monta em `payloadForContextMenu` — chaves
// de topo em snake_case (content_attributes, conversation_id, message_type…),
// igual o resto do app grava no Vuex. `activeContextItem.message` aqui é
// camelCase (veio de useCamelCase em MessageList.vue), então precisa
// converter — sem `deep`, igual ao payloadForContextMenu: só o nível de
// topo, o que tem dentro de content_attributes/attachments continua como
// está.
//
// Cai pro primeiro sticker do par quando ainda não clicaram em nenhuma
// flechinha (`activeContextItem` null) — de propósito, ver comentário no
// `v-if` do <ContextMenu> no template: ele precisa nascer montado com
// isOpen=false, não no mesmo instante em que vira true.
const contextMenuMessage = computed(() => {
  const message =
    activeContextItem.value?.message ?? albumItems.value[0]?.message;
  return message ? useSnakeCase(message) : null;
});

const handleTileReplyTo = () => {
  const message = activeContextItem.value?.message;
  if (!message) return;

  LocalStorage.updateJsonStore(
    LOCAL_STORAGE_KEYS.MESSAGE_REPLY_TO,
    message.conversationId,
    message.id
  );
  emitter.emit(BUS_EVENTS.TOGGLE_REPLY_TO_MESSAGE, message);
};

const handleTileReact = emoji => {
  const message = activeContextItem.value?.message;
  if (!message) return;

  store.dispatch('reactToMessage', {
    accountId: currentAccountIdGetter.value,
    messageId: message.id,
    emoji,
  });
};

const showGallery = ref(false);
const clickedAttachment = ref(null);

const openGalleryAt = item => {
  clickedAttachment.value = item.attachment;
  showGallery.value = true;
};

const galleryAttachment = computed(() =>
  clickedAttachment.value ? useSnakeCase(clickedAttachment.value) : null
);
</script>

<template>
  <BaseBubble
    class="acl-sticker-album !bg-transparent !p-0 !shadow-none overflow-visible"
    data-bubble-name="sticker-album"
    hide-meta
    hide-sender-name
  >
    <div
      class="acl-sticker-album__grid grid gap-[35px]"
      :style="{
        gridTemplateColumns: `repeat(2, minmax(0, ${IMAGE_BOX_PX}px))`,
      }"
    >
      <div
        v-for="item in albumItems"
        :key="item.attachment.id"
        class="acl-sticker-album__tile group flex min-w-0 flex-col"
      >
        <!-- Aceleres: três blocos EMPILHADOS, cada um com seu próprio
             fundo/arredondado — nome, imagem e horário NÃO dividem um
             cartão só. Print da conversa real (sticker sozinho) mostrou
             o nome com seu próprio fundinho verde/branco, separado da
             imagem por um vão — igual o horário já tinha ficado
             (commit anterior). -->
        <!-- Aceleres: wrapper SEMPRE presente (não `v-if`) mesmo quando não
             há nome resolvido — é ele que dá o `relative` pra flechinha.
             Se o `v-if` estivesse aqui em vez de no <p>, uma mensagem sem
             sender resolvido perderia a flechinha inteira, sem jeito de
             abrir o menu daquele sticker. -->
        <div class="relative">
          <p
            v-if="senderName"
            class="acl-sender-name m-0 w-full truncate rounded-lg px-[14px] py-[10px] text-[16.2px] font-semibold leading-tight text-n-slate-12 opacity-90"
            :class="tileBackgroundClass"
            :style="{ maxWidth: `${IMAGE_BOX_PX}px` }"
          >
            {{ senderName }}
          </p>
          <!-- Aceleres: flechinha de cada sticker do par — mesmo visual
               (badge circular) e mesmo gatilho de hover que a bolha de
               sticker sozinho já usa (ver `isStickerBubble` em
               Message.vue). Fica em cima do NOME (canto sup. direito do
               tile), não da imagem — pedido explícito, já que agora nome
               e imagem são blocos separados. Aparece só no hover DESTE
               tile (`group`/`group-hover`), não do par inteiro, já que
               cada sticker é sua própria mensagem. Abre o MESMO
               ContextMenu que a bolha normal usa, apontando pra esse
               sticker especificamente. -->
          <button
            type="button"
            aria-label="Abrir menu do sticker"
            class="skip-context-menu absolute right-1.5 top-0 z-20 flex h-[44px] w-[44px] items-center justify-center rounded-full bg-transparent text-n-slate-11 opacity-0 transition-opacity duration-150 group-hover:opacity-100 focus-visible:opacity-100"
            @click.stop="openTileContextMenu($event, item)"
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="3.4"
              stroke-linecap="round"
              stroke-linejoin="round"
              class="pointer-events-none block"
            >
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </button>
        </div>
        <!-- Aceleres: SEM tileBackgroundClass aqui de propósito — o cartão
             da imagem fica transparente, deixando o papel de parede do
             chat aparecer através das partes vazadas do sticker (a maioria
             é webp com fundo transparente). O verde/branco fica só nas
             pills de nome e horário, que são texto e precisam de fundo pra
             ler bem. -->
        <div
          class="acl-sticker-album__card relative mt-1 overflow-hidden"
          :class="tileRoundedClass"
        >
          <div
            class="flex aspect-square w-full items-center justify-center overflow-hidden"
            :style="{
              maxWidth: `${IMAGE_BOX_PX}px`,
              maxHeight: `${IMAGE_BOX_PX}px`,
            }"
          >
            <img
              class="block max-h-full max-w-full object-contain skip-context-menu cursor-pointer"
              :src="item.attachment.dataUrl"
              loading="lazy"
              @click.stop="openGalleryAt(item)"
            />
          </div>
        </div>
        <!-- Aceleres: pill do horário, separada do cartão (mt-1 de vão),
             encolhida ao próprio conteúdo (self-end) e com fundo/cor
             próprios — igual ao sticker sozinho. -->
        <div
          class="mt-1 flex items-center gap-0.5 self-end rounded-lg px-2 py-0.5 text-[11px] leading-none text-n-slate-11"
          :class="tileBackgroundClass"
        >
          <time>{{ readableTime(item.message) }}</time>
          <MessageStatus
            v-if="statusToShow(item.message)"
            :status="statusToShow(item.message)"
          />
        </div>
      </div>
    </div>
  </BaseBubble>
  <GalleryView
    v-if="showGallery && galleryAttachment"
    v-model:show="showGallery"
    :attachment="galleryAttachment"
    :all-attachments="albumGalleryAttachments"
    @close="() => (showGallery = false)"
  />
  <!-- Aceleres: MONTA JÁ, mesmo antes de clicar em qualquer flechinha —
       não depende de `showTileContextMenu`. O componente só liga seus
       listeners de auto-fechar (scroll, clique fora, Esc) num `watch`
       de `isOpen` SEM `immediate`, ou seja: só dispara numa TRANSIÇÃO
       false→true depois de montado. Se o componente nascesse junto com
       o primeiro clique (isOpen já true de cara), essa transição nunca
       aconteceria e o menu abriria "surdo" pro scroll na primeira vez —
       era exatamente esse o bug reportado. -->
  <ContextMenu
    v-if="contextMenuMessage"
    :context-menu-position="tileContextMenuPosition"
    :is-open="showTileContextMenu"
    :enabled-options="tileEnabledOptions"
    :message="contextMenuMessage"
    hide-button
    :current-user-id="currentUserId"
    @close="closeTileContextMenu"
    @reply-to="handleTileReplyTo"
    @react="handleTileReact"
  />
</template>
